import {
  BulkAssignInput,
  IRemediationRepository,
  RemediationQueueParams,
  SaveCorrectionInput,
  SaveCorrectionsBatchInput,
  Section2DocumentInput,
} from '../ports/outbound/IRemediationRepository';
import { EmployeeSubmitInput, EmployeeVerifyInput } from '../domain/entities/Remediation';

/** One use-case per backend `/remediation/*` operation (mirrors batch_processing). */

export class GetRemediationQueueUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(params: RemediationQueueParams) {
    return this.repo.getQueue(params);
  }
}

export class GetRemediationRecordUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string) {
    return this.repo.getRecord(id);
  }
}

export class SaveCorrectionUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string, input: SaveCorrectionInput) {
    return this.repo.saveCorrection(id, input);
  }
}

export class SaveCorrectionsBatchUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string, input: SaveCorrectionsBatchInput) {
    return this.repo.saveCorrectionsBatch(id, input);
  }
}

export class MarkSection2SignedUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string, justification: string) {
    return this.repo.markSection2Signed(id, justification);
  }
}

export class SaveSection2DocumentUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string, input: Section2DocumentInput) {
    return this.repo.saveSection2Document(id, input);
  }
}

export class UploadDocumentUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string, file: string, fileName: string) {
    return this.repo.uploadDocument(id, file, fileName);
  }
}

export class DismissFindingUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string, findingId: string, justification: string) {
    return this.repo.dismissFinding(id, findingId, justification);
  }
}

export class AssignRemediationUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string, assigneeUserId?: string) {
    return this.repo.assign(id, assigneeUserId);
  }
}

export class BulkAssignRemediationUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(input: BulkAssignInput) {
    return this.repo.bulkAssign(input);
  }
}

export class SendToEmployeeUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string, employeeEmail: string, employeePhone?: string) {
    return this.repo.sendToEmployee(id, employeeEmail, employeePhone);
  }
}

export class SkipEmployeeUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string, reason: string) {
    return this.repo.skipEmployee(id, reason);
  }
}

export class AddSection1ItemUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string, fieldPath: string, note?: string) {
    return this.repo.addSection1Item(id, fieldPath, note);
  }
}

export class RemoveSection1ItemUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string, findingId: string) {
    return this.repo.removeSection1Item(id, findingId);
  }
}

export class AssignLocationUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string, locationId: string) {
    return this.repo.assignLocation(id, locationId);
  }
}

export class CompleteRemediationUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string) {
    return this.repo.complete(id);
  }
}

export class GetRemediationVerificationUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string) {
    return this.repo.getVerification(id);
  }
}

export class GetRemediationAuditUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(id: string) {
    return this.repo.getAudit(id);
  }
}

export class GetAssignableUsersUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(accountId: string) {
    return this.repo.getAssignableUsers(accountId);
  }
}

export class EmployeeIdentityRequirementsUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(token: string) {
    return this.repo.employeeIdentityRequirements(token);
  }
}

export class EmployeeVerifyUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(input: EmployeeVerifyInput) {
    return this.repo.employeeVerify(input);
  }
}

export class EmployeeSubmitUseCase {
  constructor(private readonly repo: IRemediationRepository) {}
  execute(input: EmployeeSubmitInput) {
    return this.repo.employeeSubmit(input);
  }
}
